import { useEffect, useRef } from 'react';
import Globe from 'globe.gl';

export interface ThreatPoint {
  id: string;
  lat: number;
  lng: number;
  level: 'critical' | 'high' | 'medium' | 'low' | 'none';
  count: number;
  city?: string;
  country?: string;
  /** Optional secondary label (e.g. IP) for hex tooltips */
  label?: string;
}

interface Globe3DProps {
  points: ThreatPoint[];
  className?: string;
  centerLat?: number;
  centerLng?: number;
  /** Focused threat id — flies camera + pulse ring */
  selectedId?: string | null;
  /**
   * Bump to re-trigger fly-to even when selectedId is unchanged
   * (only used when selection is non-null).
   */
  focusToken?: number;
  onPointClick?: (point: ThreatPoint) => void;
  /** Default true; paused while a point is selected */
  autoRotate?: boolean;
}

const LEVEL_COLORS: Record<string, string> = {
  critical: '#dc2626',
  high: '#f97316',
  medium: '#facc15',
  low: '#22c55e',
  none: '#6b7280',
};

// Home marker (where arcs converge)
const HOME_LAT = 49.2827; // Vancouver
const HOME_LNG = -123.1207;

/** Shared surface altitude so pin (3D point) and arc root share the same height */
const SURFACE_ALT = 0.012;

const LEVEL_RANK: Record<string, number> = {
  critical: 4,
  high: 3,
  medium: 2,
  low: 1,
  none: 0,
};

function pickBestPoint(points: ThreatPoint[]): ThreatPoint | null {
  if (!points.length) return null;
  return [...points].sort(
    (a, b) => (LEVEL_RANK[b.level] ?? 0) - (LEVEL_RANK[a.level] ?? 0),
  )[0];
}

/** Full arc field: every point always; selected highlighted, peers dimmed. */
function buildArcs(points: ThreatPoint[], selectedId: string | null | undefined) {
  return points.map((point) => ({
    startLat: point.lat,
    startLng: point.lng,
    // Lift slightly so the tube roots at the same height as the 3D marker
    startAlt: SURFACE_ALT,
    endLat: HOME_LAT,
    endLng: HOME_LNG,
    endAlt: SURFACE_ALT,
    level: point.level,
    highlight: selectedId != null && point.id === selectedId,
    order: selectedId != null && point.id === selectedId ? 1 : 0,
  }));
}

/**
 * HTML shell for CSS2DObject.
 * CRITICAL: never set `transform` on the root element — three.js CSS2DRenderer
 * owns that for screen projection. Animate only child nodes.
 */
function makeHtmlMarker(opts: {
  kind: 'home' | 'selected';
  level?: string;
  label?: string;
}): HTMLDivElement {
  const root = document.createElement('div');
  // Root: no transform. CSS2D applies translate(-50%,-50%) translate(x,y) here.
  root.style.pointerEvents = 'none';

  const color = LEVEL_COLORS[opts.level || ''] || '#6366f1';

  if (opts.kind === 'home') {
    root.innerHTML = `
      <div class="sg-html-inner" style="
        display:flex;flex-direction:column;align-items:center;gap:2px;
        margin-left:-8px;margin-top:-8px;
      ">
        <div class="sg-pulse-dot" style="
          width:14px;height:14px;
          background:radial-gradient(circle,#6366f1,#6366f1 30%,transparent 70%);
          border:2px solid #6366f1;border-radius:50%;
          box-shadow:0 0 18px #6366f1,0 0 36px #6366f1aa;
        "></div>
      </div>
    `;
  } else {
    const safeLabel = (opts.label || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;');
    root.innerHTML = `
      <div class="sg-html-inner" style="
        display:flex;flex-direction:column;align-items:center;gap:4px;
        margin-left:-9px;margin-top:-9px;
      ">
        <div class="sg-pulse-dot" style="
          width:18px;height:18px;border-radius:50%;
          background:${color};border:2px solid #fff;
          box-shadow:0 0 16px ${color},0 0 32px ${color}99;
        "></div>
        ${
          safeLabel
            ? `<div style="
                font:600 10px/1.2 ui-monospace,monospace;color:#fff;
                background:rgba(18,18,26,0.92);border:1px solid ${color}88;
                padding:3px 6px;border-radius:4px;white-space:nowrap;
                max-width:140px;overflow:hidden;text-overflow:ellipsis;
              ">${safeLabel}</div>`
            : ''
        }
      </div>
    `;
  }

  if (!document.getElementById('sg-globe-pulse-style')) {
    const style = document.createElement('style');
    style.id = 'sg-globe-pulse-style';
    // Opacity/box-shadow only — NEVER transform (would fight CSS2D)
    style.textContent = `
      @keyframes sg-pulse-opacity {
        0%, 100% { opacity: 1; box-shadow: 0 0 12px currentColor; }
        50% { opacity: 0.75; box-shadow: 0 0 22px currentColor; }
      }
      .sg-pulse-dot {
        animation: sg-pulse-opacity 1.4s ease-in-out infinite;
      }
    `;
    document.head.appendChild(style);
  }

  return root;
}

export function Globe3D({
  points,
  className = '',
  centerLat = 30,
  centerLng = 0,
  selectedId = null,
  focusToken = 0,
  onPointClick,
  autoRotate = true,
}: Globe3DProps) {
  const containerRef = useRef<HTMLDivElement>(null);
  const globeRef = useRef<any>(null);
  const onPointClickRef = useRef(onPointClick);
  const pointsRef = useRef(points);
  const selectedIdRef = useRef(selectedId);
  const resumeRotateTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  onPointClickRef.current = onPointClick;
  pointsRef.current = points;
  selectedIdRef.current = selectedId;

  // Init globe once
  useEffect(() => {
    if (!containerRef.current || globeRef.current) return;

    const globe = new Globe(containerRef.current)
      .globeImageUrl('//unpkg.com/three-globe/example/img/earth-night.jpg')
      .backgroundColor('rgba(0,0,0,0)')
      .showAtmosphere(true)
      .atmosphereColor('#6366f1')
      .atmosphereAltitude(0.15)

      // Hex columns
      .hexBinPointsData([])
      .hexBinPointWeight('count')
      .hexAltitude((d: any) => {
        const pts = (d.points || []) as ThreatPoint[];
        const sel = selectedIdRef.current;
        const isSelectedBin = !!sel && pts.some((p) => p.id === sel);
        if (sel && !isSelectedBin) return Math.min(0.16, 0.03 + d.sumWeight * 0.0007);
        return Math.min(0.28, 0.04 + d.sumWeight * 0.001) * (isSelectedBin ? 1.15 : 1);
      })
      .hexTopColor((d: any) => {
        const pts = (d.points || []) as ThreatPoint[];
        const sel = selectedIdRef.current;
        const isSelectedBin = !!sel && pts.some((p) => p.id === sel);
        if (sel && !isSelectedBin) {
          const level = pts[0]?.level || 'low';
          return (LEVEL_COLORS[level] || LEVEL_COLORS.low) + '55';
        }
        const level = (isSelectedBin ? pts.find((p) => p.id === sel) : pts[0])?.level || 'low';
        return LEVEL_COLORS[level] || LEVEL_COLORS.low;
      })
      .hexSideColor((d: any) => {
        const pts = (d.points || []) as ThreatPoint[];
        const sel = selectedIdRef.current;
        const isSelectedBin = !!sel && pts.some((p) => p.id === sel);
        const level = (isSelectedBin ? pts.find((p) => p.id === sel) : pts[0])?.level || 'low';
        const color = LEVEL_COLORS[level] || LEVEL_COLORS.low;
        if (sel && !isSelectedBin) return color + '30';
        return isSelectedBin ? color + 'bb' : color + '55';
      })
      .hexBinResolution(3)
      .hexLabel((d: any) => {
        const pts = (d.points || []) as ThreatPoint[];
        const point = pickBestPoint(pts) || pts[0];
        if (!point) return '';
        const color = LEVEL_COLORS[point.level] || LEVEL_COLORS.low;
        const extra =
          pts.length > 1
            ? `<div style="color:#9ca3af;margin-top:4px">${pts.length} threats here</div>`
            : point.label
              ? `<div style="color:#9ca3af;font-family:ui-monospace,monospace;margin-top:4px">${point.label}</div>`
              : '';
        return `
          <div style="
            background: rgba(18, 18, 26, 0.95);
            padding: 8px 12px; border-radius: 6px;
            border: 1px solid rgba(99, 102, 241, 0.35);
            color: white; font-family: system-ui, sans-serif; font-size: 12px;
            pointer-events: none;
          ">
            <div style="font-weight: 600; margin-bottom: 4px;">
              ${point.city || 'Unknown'}${point.country ? `, ${point.country}` : ''}
            </div>
            <div style="color: #9ca3af;">Level: <span style="color: ${color}">${point.level}</span></div>
            <div style="color: #9ca3af;">Weight: ${d.sumWeight}</div>
            ${extra}
            <div style="color: #818cf8; margin-top: 6px; font-size: 11px;">Click to select</div>
          </div>
        `;
      })
      .onHexClick((d: any) => {
        const pts = (d?.points || []) as ThreatPoint[];
        const preferred = pickBestPoint(pts);
        if (preferred) onPointClickRef.current?.(preferred);
      })

      // True 3D points — share lat/lng/alt with arc roots (no CSS2D drift)
      .pointsData([])
      .pointLat('lat')
      .pointLng('lng')
      .pointAltitude('alt')
      .pointRadius((d: any) => (d.kind === 'selected' ? 0.55 : 0.35))
      .pointColor((d: any) => d.color || '#6366f1')
      .pointsMerge(false)

      // Arcs: start = threat (same lat/lng/alt as 3D pin), end = home
      .arcsData([])
      .arcStartLat('startLat')
      .arcStartLng('startLng')
      .arcStartAltitude('startAlt')
      .arcEndLat('endLat')
      .arcEndLng('endLng')
      .arcEndAltitude('endAlt')
      .arcColor((d: any) => {
        const color = LEVEL_COLORS[d.level] || LEVEL_COLORS.low;
        if (d.highlight) return [color, color];
        return [`${color}38`, `${color}66`];
      })
      // Solid selected arc; light dash on peers only
      .arcDashLength((d: any) => (d.highlight ? 1 : 0.35))
      .arcDashGap((d: any) => (d.highlight ? 0 : 0.4))
      .arcDashAnimateTime((d: any) => (d.highlight ? 0 : 3400))
      // Thin tubes so the root sits under the pin instead of a fat offset blob
      .arcStroke((d: any) => (d.highlight ? 0.45 : 0.28))
      .arcAltitude((d: any) => (d.highlight ? 0.14 : 0.11))
      .arcAltitudeAutoScale(false)
      .arcsTransitionDuration(0)

      // Selection ring (lat/lng only — sits on surface)
      .ringsData([])
      .ringColor(() => (t: number) => `rgba(99, 102, 241, ${Math.sqrt(1 - t)})`)
      .ringMaxRadius(2.8)
      .ringPropagationSpeed(2.2)
      .ringRepeatPeriod(900)

      // HTML: labels only (no transform on root)
      .htmlElementsData([{ lat: HOME_LAT, lng: HOME_LNG, kind: 'home' }])
      .htmlLat('lat')
      .htmlLng('lng')
      .htmlAltitude(SURFACE_ALT)
      .htmlElement((d: any) =>
        makeHtmlMarker({
          kind: d.kind === 'selected' ? 'selected' : 'home',
          level: d.level,
          label: d.label,
        }),
      )
      .htmlTransitionDuration(0);

    globe.pointOfView({ lat: centerLat, lng: centerLng, altitude: 2.2 }, 0);

    const controls = globe.controls();
    controls.autoRotate = autoRotate;
    controls.autoRotateSpeed = 0.4;
    controls.enableZoom = true;
    controls.minDistance = 180;
    controls.maxDistance = 800;

    globeRef.current = globe;

    const resize = () => {
      if (containerRef.current && globeRef.current) {
        const w = containerRef.current.offsetWidth;
        const h = containerRef.current.offsetHeight;
        if (w > 0 && h > 0) {
          globeRef.current.width(w);
          globeRef.current.height(h);
        }
      }
    };
    setTimeout(resize, 0);
    setTimeout(resize, 100);
    window.addEventListener('resize', resize);

    return () => {
      window.removeEventListener('resize', resize);
      if (resumeRotateTimer.current) clearTimeout(resumeRotateTimer.current);
      globeRef.current = null;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Sync hex + full arcs + 3D points
  useEffect(() => {
    const globe = globeRef.current;
    if (!globe) return;

    globe.hexBinPointsData(points.map((p) => ({ ...p })));

    const arcs = buildArcs(points, selectedId).sort((a, b) => a.order - b.order);
    globe.arcsData(arcs);

    // 3D anchors: home always + selected threat (same coords as arc start)
    const selected = selectedId
      ? points.find((p) => p.id === selectedId) ?? null
      : null;
    const pointMarkers: Array<{
      lat: number;
      lng: number;
      alt: number;
      color: string;
      kind: string;
    }> = [
      {
        lat: HOME_LAT,
        lng: HOME_LNG,
        alt: SURFACE_ALT,
        color: '#6366f1',
        kind: 'home',
      },
    ];
    if (selected) {
      pointMarkers.push({
        lat: selected.lat,
        lng: selected.lng,
        alt: SURFACE_ALT,
        color: LEVEL_COLORS[selected.level] || '#f97316',
        kind: 'selected',
      });
    }
    globe.pointsData(pointMarkers);
  }, [points, selectedId]);

  // Focus camera + rings + HTML label (arcs/points already synced above)
  useEffect(() => {
    const globe = globeRef.current;
    if (!globe) return;

    const controls = globe.controls();
    if (resumeRotateTimer.current) {
      clearTimeout(resumeRotateTimer.current);
      resumeRotateTimer.current = null;
    }

    const pts = pointsRef.current;
    const selected = selectedId
      ? pts.find((p) => p.id === selectedId) || null
      : null;

    // Re-assert full arc field on every focus change (deselect never leaves single-arc)
    const arcs = buildArcs(pts, selectedId).sort((a, b) => a.order - b.order);
    globe.arcsData(arcs);

    if (selected) {
      controls.autoRotate = false;
      globe.pointOfView(
        { lat: selected.lat, lng: selected.lng, altitude: 1.55 },
        1100,
      );
      globe.ringsData([
        {
          lat: selected.lat,
          lng: selected.lng,
          maxR: 3.2,
          propagationSpeed: 2.4,
          repeatPeriod: 800,
        },
      ]);
      // HTML label at exact same lat/lng as 3D point + arc start
      globe.htmlElementsData([
        { lat: HOME_LAT, lng: HOME_LNG, kind: 'home' },
        {
          lat: selected.lat,
          lng: selected.lng,
          kind: 'selected',
          level: selected.level,
          label: selected.label || selected.city || selected.country || '',
        },
      ]);
      globe.pointsData([
        {
          lat: HOME_LAT,
          lng: HOME_LNG,
          alt: SURFACE_ALT,
          color: '#6366f1',
          kind: 'home',
        },
        {
          lat: selected.lat,
          lng: selected.lng,
          alt: SURFACE_ALT,
          color: LEVEL_COLORS[selected.level] || '#f97316',
          kind: 'selected',
        },
      ]);

      resumeRotateTimer.current = setTimeout(() => {
        if (globeRef.current) {
          globeRef.current.controls().autoRotate = autoRotate;
          globeRef.current.controls().autoRotateSpeed = 0.25;
        }
      }, 8000);
    } else {
      globe.ringsData([]);
      globe.htmlElementsData([{ lat: HOME_LAT, lng: HOME_LNG, kind: 'home' }]);
      globe.pointsData([
        {
          lat: HOME_LAT,
          lng: HOME_LNG,
          alt: SURFACE_ALT,
          color: '#6366f1',
          kind: 'home',
        },
      ]);
      controls.autoRotate = autoRotate;
      controls.autoRotateSpeed = 0.4;
    }

    return () => {
      if (resumeRotateTimer.current) {
        clearTimeout(resumeRotateTimer.current);
        resumeRotateTimer.current = null;
      }
    };
  }, [selectedId, focusToken, autoRotate]);

  return (
    <div
      ref={containerRef}
      className={className}
      style={{ width: '100%', height: '100%' }}
      data-selected-id={selectedId || undefined}
    />
  );
}
